import TranslationKeys from "../../../contexts/I18nContext/translationKeys";
import { WhmAutocomplete, WhmBox, WhmTextField } from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../Translation/Translate";
import { InfoCard } from "../shared/InfoCard";
import React from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { InvestorDto } from "../../../data/dtos/investor.dto";
import { statesUS } from "../../Registration/countriesList";
import { InvestorTypes } from "../../../data/types/investors";
import {
  columnBox,
  placeholderTextFieldStyle,
  textFieldStyle,
  wrapBox,
} from "../shared/styles";
import CountryTextField from "../../shared/CountryTextField/CountryTextField";

interface AddressCardProps {
  investorInfoQuery: UseQueryResult<InvestorDto, Error>;
}

export const AddressCard: React.FC<
  React.PropsWithChildren<AddressCardProps>
> = ({ investorInfoQuery }) => {
  const isEntity =
    investorInfoQuery.data?.investorType === InvestorTypes.ENTITY;

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            isEntity
              ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_TITLE
              : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            isEntity
              ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_SUBTITLE
              : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_SUBTITLE
          }
        />
      }
      footer={
        <TranslateHtml
          translationKey={
            isEntity
              ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_FOOTER
              : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_FOOTER
          }
        />
      }
    >
      <WhmBox
        component="form"
        sx={{
          ...columnBox,
          marginTop: "32px",
        }}
        noValidate
        autoComplete="off"
      >
        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
          }}
        >
          <WhmTextField
            inputProps={{
              "data-test-id": "profile-information-address-address-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_ADDRESS
                }
              />
            }
            value={investorInfoQuery.data?.details.address?.street}
            sx={textFieldStyle}
            disabled
          />
          <WhmTextField
            inputProps={{
              "data-test-id":
                "profile-information-address-additional-info-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_ADDITIONAL_INFO
                }
              />
            }
            value={investorInfoQuery.data?.details.address?.entrance}
            sx={textFieldStyle}
            disabled
          />
          <WhmTextField
            inputProps={{
              "data-test-id": "profile-information-address-city-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_CITY
                }
              />
            }
            value={investorInfoQuery.data?.details.address?.city}
            sx={textFieldStyle}
            disabled
          />
        </WhmBox>

        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
            marginTop: "16px",
          }}
        >
          <WhmTextField
            inputProps={{
              "data-test-id": "profile-information-address-zip-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_POSTAL_CODE
                }
              />
            }
            value={investorInfoQuery.data?.details.address?.zip}
            sx={textFieldStyle}
            disabled
          />

          <CountryTextField
            name={"countryCode"}
            value={investorInfoQuery.data?.details.address?.countryCode ?? ""}
            dataTestId="profile-information-address-country-field"
            disabled
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_COUNTRY
                }
              />
            }
          />

          {investorInfoQuery.data?.details.address?.countryCode === "US" ? (
            <WhmAutocomplete
              options={statesUS}
              value={statesUS.find(
                (state) =>
                  state.value ===
                  investorInfoQuery.data?.details.address?.state,
              )}
              fullWidth
              disabled
              renderInput={(props) => (
                <WhmTextField
                  {...props}
                  inputProps={{
                    // eslint-disable-next-line react/prop-types
                    ...props.inputProps,
                    "data-test-id": "profile-information-address-state-field",
                  }}
                  label={
                    <Translate
                      translationKey={
                        TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_STATE
                      }
                    />
                  }
                  name="state"
                />
              )}
            />
          ) : (
            <WhmBox sx={placeholderTextFieldStyle} />
          )}
        </WhmBox>
      </WhmBox>
    </InfoCard>
  );
};
